import React, { createRef, useEffect, useRef, useState } from "react";
import { Layer, Map, MapRef, Source } from "react-map-gl";
import maplibregl from "maplibre-gl";
import {
  isMapboxURL,
  transformMapboxUrl,
} from "maplibregl-mapbox-request-transformer";
import "intersection-observer";
import "maplibre-gl/dist/maplibre-gl.css";
import "../styles/app.scss";
import { useScrollPosition } from "@n8tb1t/use-scroll-position";
import { Paragraph, splitParagraphs } from "./ArticleBody";

/**
 * This is a public access token connected to The City Reporter's MapBox account:
 */
export const MAPBOX_TOKEN =
  "pk.eyJ1IjoidGhlLWNpdHkiLCJhIjoiY2xhMWVsNDY3MDJoYTNya2ptYWZpZW15dyJ9.iv4eTGq5GylMTUcYH16Big";

/**
 * This is a link to our custom MapBox Studio basemap style:
 */
export const MAPBOX_STYLE_URL =
  "mapbox://styles/the-city/cloq9dvvp00ey01qn5jkgcqzb";

/**
 * Since our custom style does not include links for map sprites, let's specify a fallback map style to load in:
 */
const FALLBACK_STYLE_URL_ROOT =
  "https://tiles.basemaps.cartocdn.com/gl/positron-gl-style/sprite@2x";

export const transformStyleRequest = (url: string, resourceType: string) => {
  if (resourceType === "SpriteImage") {
    return {
      url: `${FALLBACK_STYLE_URL_ROOT}.png`,
    };
  }
  if (resourceType === "SpriteJSON") {
    return {
      url: `${FALLBACK_STYLE_URL_ROOT}.json`,
    };
  }
  if (isMapboxURL(url)) {
    return transformMapboxUrl(url, resourceType, MAPBOX_TOKEN);
  }
  return { url };
};

export const MapLoadingScreen = () => (
  <div className="loading-screen map">
    <div>
      <p className="mb-2">Loading...</p>
      <progress className="progress is-medium" max="100" />
    </div>
  </div>
);

const NUMBER_OF_MAP_SLIDES = 4;
/**
 * When each paragraph gets within this distance (in px) of the top of the viewport, animations are triggered.
 */
const ANIMATION_TRIGGER_BUFFER = 500;

/**
 * Total length of time map animations take, in milliseconds.
 */
const ANIMATION_DURATION = 1000;

/**
 * A "blank" set of x and y scroll coordinates to initialize our react states with.
 * The values here are meaningless, just making sure the y coordinate is larger than our trigger buffer.
 */
const BLANK_SCROLL_POSITION = {
  x: 0,
  y: ANIMATION_TRIGGER_BUFFER + 100,
};

export const MapSequence: React.FC<{
  slideContent: string;
}> = ({ slideContent }) => {
  const slideElements = splitParagraphs(slideContent);

  // Map data that goes on the map:

  const [areaData, setAreaData] = useState(null);
  const [locationData, setLocationData] = useState(null);

  const [statesData, setStatesData] = useState(null);
  const [stateLabelsData, setStateLabelsData] = useState(null);

  /**
   * The number of the current slide the user is looking at, starting with 0:
   */
  const [currentSlide, setCurrentSlide] = useState(0);

  /**
   * The scroll positions of each of the paragraphs overlayed on the map:
   */
  const [paragraphPosition, setParagraphPosition] = useState({
    0: BLANK_SCROLL_POSITION,
    1: BLANK_SCROLL_POSITION,
    2: BLANK_SCROLL_POSITION,
  });

  /**
   * Add data to map on initial mount of the react component:
   */
  useEffect(() => {
    fetch("./data/arrests-by-area.geojson", {
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    })
      .then((resp) => resp.json())
      .then((json) => {
        setAreaData(json);
        console.log("Loaded arrests by area data");
      })
      .catch((err) =>
        console.error("Could not load arrests by area data", err),
      );
    fetch("./data/arrests-by-location.geojson", {
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    })
      .then((resp) => resp.json())
      .then((json) => {
        setLocationData(json);
        console.log("Loaded arrests by location data");
      })
      .catch((err) =>
        console.error("Could not load arrests by location data", err),
      );
    fetch("./data/states.geojson", {
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    })
      .then((resp) => resp.json())
      .then((json) => {
        setStatesData(json);
        console.log("Loaded states data");
      })
      .catch((err) => console.error("Could not load states data", err));
    fetch("./data/state-labels.geojson", {
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    })
      .then((resp) => resp.json())
      .then((json) => {
        setStateLabelsData(json);
        console.log("Loaded state labels data");
      })
      .catch((err) => console.error("Could not load state labels data", err));
  }, []);

  const mapRef = useRef<MapRef>();
  const paragraphRefs = useRef([createRef(), createRef(), createRef()]);

  /**
   * This custom React hook keeps track of a specific paragraph in our scrolly
   * and continuously updates its scroll position saved in state.
   */
  const useParagraphTracker = (paragraphNumber: number) =>
    useScrollPosition(
      ({ currPos }) => {
        setParagraphPosition((prevValues) => ({
          ...prevValues,
          [paragraphNumber]: currPos,
        }));
      },
      [],
      //@ts-ignore
      paragraphRefs.current[paragraphNumber],
    );

  // Sadly, the `useScrollPosition` hook can only be used one at a time to update
  // states based on the position of an HTML element. Therefore, we need to implement
  // this hook once for each paragraph we are tracking the location of:
  useParagraphTracker(0);
  useParagraphTracker(1);
  useParagraphTracker(2);

  /**
   * This hook determines which "current slide" the user is viewing by
   * looking at the scroll positions of each paragraph:
   */
  useEffect(() => {
    const currentSlide =
      (paragraphPosition[0].y < ANIMATION_TRIGGER_BUFFER ? 1 : 0) +
      (paragraphPosition[1].y < ANIMATION_TRIGGER_BUFFER ? 1 : 0) +
      (paragraphPosition[2].y < ANIMATION_TRIGGER_BUFFER ? 1 : 0);
    setCurrentSlide(currentSlide);
  }, [paragraphPosition]);

  /**
   * This hook triggers custom animations based on when the current slide changes:
   */
  useEffect(() => {
    // Animate zoom level change when the first paragraph scrolls by:
    if (currentSlide === 1) {
      mapRef.current?.fitBounds([-74.63562, 39.770548, -73.449097, 41.832735], {
        duration: ANIMATION_DURATION,
      });
    }
    if (currentSlide === 2) {
      // Zoom to center of NYC and show the entirety of the five boroughs:
      mapRef.current?.fitBounds(
        [-74.255735, 40.496044, -73.700272, 40.915256], // bounds for NYC
        {
          duration: ANIMATION_DURATION,
        },
      );
    }
    if (currentSlide === 3) {
      mapRef.current?.fitBounds(
        [-73.876104, 40.730543, -73.841944, 40.760553], // bounds for Corona, Queens
        {
          duration: ANIMATION_DURATION,
        },
      );
    }
  }, [currentSlide]);

  return !!areaData && !!locationData && !!statesData && !!stateLabelsData ? (
    <div
      className="scrolly-container map-wrapper is-flex is-flex-direction-column is-justify-content-space-between is-align-items-center is-doubled my-4"
      style={{
        height: `${100 + NUMBER_OF_MAP_SLIDES * 100}vh`,
      }}
    >
      <div className="scrolly-image">
        <div className="map-container is-flex is-flex-direction-column is-align-items-center is-justify-content-center">
          <Map
            //@ts-ignore
            ref={mapRef}
            mapLib={maplibregl}
            initialViewState={{
              longitude: -73.932495,
              latitude: 40.697299,
              zoom: 6,
            }}
            mapStyle={MAPBOX_STYLE_URL}
            transformRequest={transformStyleRequest}
            mapboxAccessToken={MAPBOX_TOKEN}
            interactive={false}
            cursor="auto"
          >
            <Source id="states-data" type="geojson" data={statesData}>
              <Layer
                id="states"
                type="line"
                source="states-data"
                // Light white outline to make state boundaries more visible:
                // ALSO: add central state name labels based on "NAME" property
                paint={{
                  "line-color": "#dde5e8", // The City Reporter Light Grey
                  "line-width": 1,
                  "line-dasharray": [5, 4], // Simple dash array
                  "line-opacity": currentSlide < 2 ? 0.25 : 0,
                }}
              />
            </Source>
            <Source
              id="state-labels-data"
              type="geojson"
              data={stateLabelsData}
            >
              <Layer
                id="state-labels"
                type="symbol"
                source="state-labels-data"
                // add central state name labels based on "NAME" property
                paint={{
                  "text-color": "#dde5e8", // The City Reporter Light Grey
                  "text-opacity": currentSlide < 2 ? 0.35 : 0,
                }}
                layout={{
                  "text-field": ["get", "NAME"],
                  "text-transform": "uppercase",
                  "text-font": ["Open Sans Regular, Arial Unicode MS Regular"],
                  "text-size": 14,
                  "text-allow-overlap": false,
                  "text-optional": true,
                  "text-letter-spacing": 0.2,
                }}
              />
            </Source>
            <Source id="arrests-by-area-data" type="geojson" data={areaData}>
              <Layer
                id="arrests-by-area"
                type="circle"
                source="arrests-by-area-data"
                paint={{
                  "circle-color": "#cd2927",
                  "circle-radius": [
                    "interpolate",
                    ["linear"],
                    ["get", "ct_arrests"],
                    0,
                    0,
                    1,
                    5,
                    10,
                    10,
                    20,
                    15,
                  ], // Between 5 and 20 based on the "ct_arrests" property of each feature,
                  "circle-opacity":
                    currentSlide < 2 ? 0.4 : currentSlide === 2 ? 0.15 : 0,
                  "circle-radius-transition": {
                    duration: 1500,
                  },
                  "circle-stroke-width": 0.2, // Width in pixels
                  "circle-stroke-color": "#cd2927",
                  "circle-stroke-opacity": currentSlide < 2 ? 1 : 0,
                }}
              />
            </Source>
            <Source
              id="arrests-by-location-data"
              type="geojson"
              data={locationData}
            >
              <Layer
                id="arrests-by-location"
                type="circle"
                source="arrests-by-location-data"
                paint={{
                  "circle-color": "#cd2927",
                  "circle-radius": currentSlide === 3 ? 15 : 8,
                  "circle-opacity": currentSlide >= 2 ? 0.8 : 0,
                  "circle-radius-transition": {
                    duration: 1500,
                  },
                  "circle-stroke-width": 0.2, // Width in pixels
                  "circle-stroke-color": "#cd2927",
                  "circle-stroke-opacity": currentSlide > 2 ? 1 : 0,
                }}
              />
            </Source>
          </Map>
        </div>
      </div>
      {slideElements.map((slide, i) => (
        <div
          key={i}
          className="scrolly-caption is-flex-direction-column is-justify-content-center p-3 mx-2"
          style={{
            marginTop: `${i + 1}00vh`,
          }}
          //@ts-ignore
          ref={paragraphRefs.current[i]}
        >
          <Paragraph text={slide} />
        </div>
      ))}
    </div>
  ) : (
    <MapLoadingScreen />
  );
};
